// =============================================================================================
// ==== Utworzony w Krok 4A >>>>

import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField,
  Chip,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Business as StructureIcon,
  QrCode as QrIcon,
  Nfc as NfcIcon,
  Clear as ClearIcon 
} from '@mui/icons-material';
import { FormSection } from '../../../types/forms';
import { NodeTreePicker } from './NodeTreePicker';

interface NodeValue {
  id: number;
  key: string;
  name: string;
}

interface NodePickerFieldProps {
  section: FormSection;
  value?: NodeValue;
  onChange: (value: NodeValue | null) => void;
  error?: string;
}

export const NodePickerField: React.FC<NodePickerFieldProps> = ({
  section,
  value,
  onChange,
  error
}) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  
  // Parse config options
  const rootNodeId = section.config?.rootNodeId;
  const showStructureButton = section.config?.structure !== false; // default true
  const showQrButton = section.config?.qr !== false; // default true  
  const showNfcButton = section.config?.nfc !== false; // default true

  // Validation
  const isRequired = !!section.validation?.required;
  const hasError = !!error;

  // Handle node selection from tree picker
  const handleNodeSelect = (selectedNode: NodeValue) => {
    onChange(selectedNode);
    setIsPickerOpen(false);
  };

  // Handle clear selection
  const handleClear = () => {
    onChange(null);
  };

  // Handle QR scan (placeholder)
  const handleQrScan = () => {
    // TODO: Implement QR scanning in Phase 4
    alert('QR Scanner będzie dostępny w kolejnej fazie rozwoju');
  };

  // Handle NFC scan (placeholder)
  const handleNfcScan = () => {
    // TODO: Implement NFC scanning in Phase 4
    alert('NFC Scanner będzie dostępny w kolejnej fazie rozwoju');
  };

  return (
    <Box sx={{ mb: 2 }}>
      {/* Label */}
      <Typography variant="subtitle1" component="label" sx={{ mb: 1, display: 'block' }}>
        {section.label}
        {isRequired && <span style={{ color: 'red' }}> *</span>}
      </Typography>

      {/* Description */}
      {section.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {section.description}
        </Typography>
      )}

      {/* Selected Value Display */}
      {value ? (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={`${value.name} (ID: ${value.id})`}
            onDelete={handleClear}
            color="primary"
            variant="outlined"
            deleteIcon={<ClearIcon />}
            sx={{ maxWidth: '100%' }}
          />
        </Box>
      ) : (
        <TextField
          fullWidth
          placeholder="Wybierz obiekt..."
          value=""
          InputProps={{ readOnly: true }}
          error={hasError}
          helperText={error}
          sx={{ mb: 2 }}
        />
      )}

      {/* Action Buttons */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {showStructureButton && (
          <Button
            variant="contained"
            startIcon={<StructureIcon />}
            onClick={() => setIsPickerOpen(true)}
            disabled={!rootNodeId}
            size="small"
          >
            Przeglądaj strukturę
          </Button>
        )}
        
        {showQrButton && (
          <Tooltip title="Funkcja dostępna w kolejnej wersji">
            <Button
              variant="outlined"
              startIcon={<QrIcon />}
              onClick={handleQrScan}
              size="small"
              disabled
            >
              Skanuj QR
            </Button>
          </Tooltip>
        )}
        
        {showNfcButton && (
          <Tooltip title="Funkcja dostępna w kolejnej wersji">
            <Button
              variant="outlined"
              startIcon={<NfcIcon />}
              onClick={handleNfcScan}
              size="small"
              disabled
            >
              Skanuj NFC
            </Button>
          </Tooltip>
        )}
      </Stack>

      {/* Validation error for missing rootNodeId */}
      {!rootNodeId && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          Błąd konfiguracji: brak rootNodeId
        </Typography>
      )}

      {/* Tree Picker Dialog */}
      <Dialog 
        open={isPickerOpen} 
        onClose={() => setIsPickerOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle>
          Wybierz obiekt
          <Typography variant="body2" color="text.secondary">
            {section.label}
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          {rootNodeId && (
            <NodeTreePicker
              rootNodeId={rootNodeId}
              onSelect={handleNodeSelect}
            />
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setIsPickerOpen(false)}>
            Anuluj
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};